import { Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { modKey } from '@/lib/platform'
import { ShortcutHelpPopover } from './ShortcutHelpPopover'
import { AllSuggestionsPopover } from './AllSuggestionsPopover'

export interface Suggestion {
  id: string
  title: string
  body: string
  tags: string[]
  variables: string[]
}

export interface AiDraftSuggestion {
  id: string
  body: string
  confidence: number | null
  based_on_canned_id: string | null
}

interface SuggestionBarProps {
  suggestions: Suggestion[]
  allSuggestions: Suggestion[]
  aiDraft: AiDraftSuggestion | null
  showShortcuts: boolean
  onPickSuggestion: (s: Suggestion) => void
  onPickDraft: (d: AiDraftSuggestion) => void
}

/**
 * Above the textarea in MessageComposer. Shows up to 6 canned-answer chips
 * (⌘/Ctrl + 1-6 to insert) plus one AI-drafted chip when available.
 * Number badges only appear when the textarea is focused — outside of focus
 * the OS captures ⌘+digit (Chrome tab switching), so the hint would be a lie.
 */
export function SuggestionBar({ suggestions, allSuggestions, aiDraft, showShortcuts, onPickSuggestion, onPickDraft }: SuggestionBarProps) {
  // Only show the "All" expander if the library has more than what we already surface.
  const hasMore = allSuggestions.length > suggestions.length
  if (suggestions.length === 0 && !aiDraft && !hasMore) return null

  return (
    <div className="flex flex-wrap items-center gap-2 px-1">
      <span className="text-xs text-muted-foreground flex items-center gap-1.5 shrink-0">
        <Sparkles className="size-3 text-primary" />
        Suggested
      </span>

      {suggestions.map((s, i) => (
        <button
          key={s.id}
          type="button"
          // Keep the textarea focused: stealing focus toggles the keyboard-shortcut
          // badges, which reflows the bar mid-click and lands the click on the wrong chip.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPickSuggestion(s)}
          title={s.body}
          className="group flex items-center gap-1.5 max-w-[280px] px-2.5 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs hover:bg-primary/20 cursor-pointer transition-colors"
        >
          <span className="truncate">{s.body}</span>
          {showShortcuts && (
            <span className="px-1.5 rounded bg-black/40 text-[10px] font-mono text-foreground border border-white/15 shrink-0">
              {modKey}{i + 1}
            </span>
          )}
        </button>
      ))}

      {aiDraft && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPickDraft(aiDraft)}
          title={`AI draft · confidence ${Math.round((aiDraft.confidence ?? 0) * 100)}%`}
          className={cn(
            'flex items-center gap-1.5 max-w-[320px] px-2.5 py-1 rounded-full text-xs cursor-pointer transition-colors',
            'bg-amber-500/10 border border-amber-500/40 text-amber-400 hover:bg-amber-500/20'
          )}
        >
          <Sparkles className="size-3 shrink-0" />
          <span className="truncate">{aiDraft.body}</span>
        </button>
      )}

      {hasMore && (
        <AllSuggestionsPopover all={allSuggestions} onPick={onPickSuggestion} />
      )}

      <div className="ml-auto shrink-0">
        <ShortcutHelpPopover />
      </div>
    </div>
  )
}
